import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/core/accordion";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import Alert from "@/components/core/alert";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import type { ColumnDef } from "@/components/core/data-grid";
import { Form, FormField, FormGroup, FormItem, FormPrepend } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/core/hover-card";
import { useModal } from "@/components/core/modals/use-modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import { userPermissions } from "@/constants/user-permissions";
import { userRoles } from "@/constants/user-roles";
import CaseFileUploadModal from "@/features/case-classification/case-file-upload-modal";
import CaseAnnexesTable from "@/features/case-evaluation/case-annexes-table";
import CaseAttachments from "@/features/case-evaluation/case-attachments";
import CaseAttachmentsTable from "@/features/case-evaluation/case-attachments-table";
import CasePaymentProof from "@/features/case-evaluation/case-payment-proof";
import CaseAmmendmentModal from "@/features/case-evaluation/components/case-ammendment-modal";
import {
	useCaseInvitePreviewMutation,
	useRecordUserLeadMutation,
	useRegenerateKeyCodeMutation,
	useRemoveCounselMutation,
} from "@/features/case-record/api/queries/case-records-query";
import { useCaseRecordCaseComplaintMutation } from "@/features/case-record/api/queries/case-records-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { useDocumentConfigMutation } from "@/features/dashboard/api/queries/document-config-queries";
import type { User } from "@/features/dashboard/types/user";
import { HasPermission, HasRole } from "@/hooks/use-auth";
import { downloadFile } from "@/utils/download-file";
import { toOrdinal } from "@/utils/format-data";
import { formatDate } from "@/utils/format-date";
import { formatFileSize } from "@/utils/format-file-size";
import { IconAlertCircle, IconDots, IconKey, IconLock, IconPencil, IconPlus, IconSettings } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { createContext, useContext, useEffect } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import DataTable from "../core/data-table";
import { Popover, PopoverContent, PopoverTrigger } from "../core/popover";
import Tooltip from "../core/tooltip";
import { FileViewer } from "../document-viewer/file-viewer";
import CaseCustomCoCounselModal from "./case-custom-co-counsel-modal";
import CaseDetailsAddDefendantModal from "./case-details-add-defendant-modal";
import CasePartyComplaintModal from "./case-party-complaint-modal";
import EditAddressModal from "./edit-address-modal";
import EditEmailModal from "./edit-email-modal";
import EditNameModal from "./edit-name-modal";
import ReplaceCounselModal from "./replace-counsel-modal";

const PartyTypeContext = createContext<"Plaintiff" | "Defendant">("Plaintiff");
function UserInfoCard({
	isCounsel = false,
	user,
	invitationCode,
	caseData,
	allowPost = false,
	showInvitationCode = true,
}: {
	user: User;
	isCounsel: boolean;
	allowPost: boolean;
	invitationCode: string;
	caseData: CaseRecord;
	showInvitationCode: boolean;
}) {
	const caseInvitePreviewMutation = useCaseInvitePreviewMutation();
	const removeCounselMutation = useRemoveCounselMutation();

	const [isDownloading, setIsDownloading] = useState(false);
	const handleDownload = async (id) => {
		setIsDownloading(true);
		const response = await caseInvitePreviewMutation.mutateAsync({
			case_id: caseData.id,
			case_record_id: id,
		});

		const url = URL.createObjectURL(response);

		downloadFile(url, "invite-letter.pdf");
		setIsDownloading(false);
	};

	const form = useForm({
		defaultValues: {
			id: 1,
		},
	});
	const caseRecordUserLeadMutation = useRecordUserLeadMutation();
	const modal = useModal();
	return (
		<Form {...form}>
			<form onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className="w-full">
				<div className="flex flex-col w-full p-4 gap-2">
					{/* <Alert type="success" allowClose={false} title="No related cases detected " icon={<IconAlertCircle size={20} stroke={2} />} /> */}
					<Alert type="success" allowClose={false}>
						<div className="flex justify-between items-center w-full">
							<div className="flex items-center gap-2">
								<IconAlertCircle size={20} stroke={2} />
								<span className="font-medium">No related cases detected</span>
							</div>
							{user.removed_at === null && (user?.role === "Counsel (D)" || user?.role === "Counsel (P)") && (
								<div>
									<DropdownMenu modal={false}>
										<DropdownMenuTrigger asChild>
											<IconSettings stroke={1.5} size={25} className="text-primary-600 hover:text-primary-400 cursor-pointer" />
										</DropdownMenuTrigger>
										<DropdownMenuContent>
											<DropdownMenuGroup>
												{user?.is_lead_counsel === false && (user?.role === "Counsel (D)" || user?.role === "Counsel (P)") && (
													<DropdownMenuItem>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<button
																type="button"
																onClick={() => {
																	modal.openModal({
																		title: "Set As Lead Counsel",
																		description: "You're about to designate this user as Lead Counsel. Do you want to proceed?",
																		type: "confirm",
																		variant: "green",
																		onConfirm: async () => {
																			await caseRecordUserLeadMutation.mutateAsync({
																				id: user.id,
																				userMain: user,
																				is_true: true,
																				user_role: user?.role,
																			});
																		},
																	});
																}}
															>
																Set As Lead Counsel
															</button>
														</div>
													</DropdownMenuItem>
												)}

												<DropdownMenuItem>
													<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
														<button
															type="button"
															onClick={() => {
																const counselModal = modal.openModal({
																	title: "Replace Counsel",
																	description: "Case Counsel",
																	size: "lg",
																	component: (
																		<ReplaceCounselModal
																			isEdit={true}
																			caseData={caseData}
																			user={user}
																			isPlaintiffCounsel={false}
																			defendants={[]}
																			onSubmit={(data) => {
																				modal.closeModal(counselModal);
																			}}
																		/>
																	),
																});
															}}
														>
															Replace Counsel
														</button>
													</div>
												</DropdownMenuItem>

												<DropdownMenuItem>
													<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
														<button
															type="button"
															onClick={() => {
																modal.openModal({
																	title: "Remove Counsel",
																	description: "You're about to remove this user as Counsel. Do you want to proceed?",
																	type: "confirm",
																	variant: "green",
																	onConfirm: async () => {
																		removeCounselMutation.mutateAsync({
																			caseRecordId: caseData.id,
																			data: user,
																		});
																	},
																});
															}}
														>
															Remove Counsel
														</button>
													</div>
												</DropdownMenuItem>
											</DropdownMenuGroup>
										</DropdownMenuContent>
									</DropdownMenu>
								</div>
							)}
						</div>
					</Alert>
					{invitationCode && (
						<div className="inline-flex flex-row items-center w-auto">
							<FormField
								control={form.control}
								name="code"
								render={({ field }) => (
									<FormItem>
										<FormGroup inputSize="sm">
											<FormPrepend>
												<IconKey size={18} stroke={2} />
											</FormPrepend>
											<Input className=" !rounded-none" autoComplete="off" readOnly defaultValue={invitationCode} {...field} />
										</FormGroup>
									</FormItem>
								)}
							/>
							<Button className="rounded-l-none" size="sm" type="button" loading={isDownloading} onClick={() => handleDownload(user.id)}>
								Download
							</Button>
						</div>
					)}
					<div className="flex flex-col w-full rounded-md border border-border/50 overflow-hidden">
						<div className="flex flex-col w-full">
							<table className="text-sm w-full">
								<tbody>
									{user?.classification === null || user?.classification === "Individual" ? (
										<>
											{user?.is_lead_counsel === true && (
												<tr className="border-b last:border-b-0 border-border/50">
													<td className="w-[1%] whitespace-nowrap bg-slate-50 font-semibold text-foreground-dark py-2 px-3">
														<Badge color="gray" size="xs" className="mr-2  text-center">
															Lead
														</Badge>
													</td>
												</tr>
											)}
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">First Name</td>
												<td className="p-3 text-foreground py-2 px-3 group">
													<div className="flex items-center justify-between gap-2">
														<span>{user?.first_name ?? ""}</span>
														<HasRole roles={[userRoles.BCC]}>
															<button
																type="button"
																onClick={() => {
																	const nameModal = modal.openModal({
																		title: "Edit Name",
																		size: "lg",
																		component: <EditNameModal user={user} />,
																	});
																}}
																className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
															>
																<IconPencil color="green" size={20} stroke={2} />
															</button>
														</HasRole>
													</div>
												</td>
											</tr>
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Middle Name</td>
												<td className="p-3 text-foreground py-2 px-3 group">
													<div className="flex items-center justify-between gap-2">
														<span>{user?.middle_name ?? ""}</span>
														<HasRole roles={[userRoles.BCC]}>
															<button
																type="button"
																onClick={() => {
																	const nameModal = modal.openModal({
																		title: "Edit Name",
																		size: "lg",
																		component: <EditNameModal user={user} />,
																	});
																}}
																className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
															>
																<IconPencil color="green" size={20} stroke={2} />
															</button>
														</HasRole>
													</div>
												</td>
											</tr>
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Last Name</td>
												<td className="p-3 text-foreground py-2 px-3 group">
													<div className="flex items-center justify-between gap-2">
														<span>{user?.last_name ?? ""}</span>
														<HasRole roles={[userRoles.BCC]}>
															<button
																type="button"
																onClick={() => {
																	const nameModal = modal.openModal({
																		title: "Edit Name",
																		size: "lg",
																		component: <EditNameModal user={user} />,
																	});
																}}
																className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
															>
																<IconPencil color="green" size={20} stroke={2} />
															</button>
														</HasRole>
													</div>
												</td>
											</tr>
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Suffix</td>
												<td className="p-3 text-foreground py-2 px-3 group">
													<div className="flex items-center justify-between gap-2">
														<span>{user?.suffix ?? ""}</span>
														<HasRole roles={[userRoles.BCC]}>
															<button
																type="button"
																onClick={() => {
																	const nameModal = modal.openModal({
																		title: "Edit Name",
																		size: "lg",
																		component: <EditNameModal user={user} />,
																	});
																}}
																className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
															>
																<IconPencil color="green" size={20} stroke={2} />
															</button>
														</HasRole>
													</div>
												</td>
											</tr>
										</>
									) : (
										<tr className="border-b last:border-b-0 border-border/50">
											<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Name</td>
											<td className="p-3 text-foreground py-2 px-3 group">
												<div className="flex items-center justify-between gap-2">
													<span>{user?.corporation ?? ""}</span>
													<HasRole roles={[userRoles.BCC]}>
														<button
															type="button"
															onClick={() => {
																const nameModal = modal.openModal({
																	title: "Edit Name",
																	size: "lg",
																	component: <EditNameModal user={user} />,
																});
															}}
															className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
														>
															<IconPencil color="green" size={20} stroke={2} />
														</button>
													</HasRole>
												</div>
											</td>
										</tr>
									)}
									{user.role !== "Defendant" ? (
										// <tr className="border-b last:border-b-0 border-border/50">
										// 	<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Email Address</td>
										// 	<td className="p-3 text-foreground py-2 px-3">{user?.user?.email ?? ""}</td>
										// </tr>
										<tr className="border-b last:border-b-0 border-border/50">
											<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Email Address</td>
											<td className="p-3 text-foreground py-2 px-3 group">
												<div className="flex items-center justify-between gap-2">
													<span>{user?.user?.email ?? ""}</span>
													<HasRole roles={[userRoles.BCC]}>
														<button
															type="button"
															onClick={() => {
																const userAddressModal = modal.openModal({
																	title: "Edit Email Address",
																	size: "lg",
																	component: <EditEmailModal user={user} />,
																});
															}}
															className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
														>
															<IconPencil color="green" size={20} stroke={2} />
														</button>
													</HasRole>
												</div>
											</td>
										</tr>
									) : null}
									<tr className="border-b last:border-b-0 border-border/50">
										<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Mobile Number</td>
										<td className="p-3 text-foreground py-2 px-3">
											{user?.mobile_number && "+639"} {user?.mobile_number}
										</td>
									</tr>
									<tr className="border-b last:border-b-0 border-border/50">
										<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Landline Number</td>
										<td className="p-3 text-foreground py-2 px-3">{user?.landline}</td>
									</tr>
									{user?.law_firm && (
										<>
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Law Firm</td>
												<td className="p-3 text-foreground py-2 px-3">{user?.law_firm}</td>
											</tr>
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Law Firm Email</td>
												<td className="p-3 text-foreground py-2 px-3">{user?.law_firm_email}</td>
											</tr>
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Law Firm Address</td>
												<td className="p-3 text-foreground py-2 px-3">{user?.lawfirm_address}</td>
											</tr>
										</>
									)}

									<tr className="border-b last:border-b-0 border-border/50">
										<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Address</td>
										<td className="p-3 text-foreground py-2 px-3 group">
											<div className="flex items-center justify-between gap-2">
												<span>{user?.address ? user?.address : user?.user?.address}</span>
												<HasRole roles={[userRoles.BCC]}>
													<button
														type="button"
														onClick={() => {
															const userAddressModal = modal.openModal({
																title: "Edit Address",
																size: "lg",
																component: <EditAddressModal user={user} />,
															});
														}}
														className="menu-button opacity-0 group-hover:opacity-100 transition-opacity duration-300"
													>
														<IconPencil color="green" size={20} stroke={2} />
													</button>
												</HasRole>
											</div>
										</td>
									</tr>
									{user?.representatives && user?.representatives.length !== 0 && (
										<tr className="border-b last:border-b-0 border-border/50">
											<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Representative / Attorney-in-fact</td>
											<td className="p-3 text-foreground py-2 px-3 group">
												<table className="p-0 m-0">
													<tbody className="p-0 m-0">
														{user?.representatives?.map((rep, repIndex) => (
															<tr key={repIndex} className="border-b last:border-b-0 border-border/50 p-0 m-0">
																<td className="text-foreground py-2 p-0 m-0">
																	<div className="flex flex-col gap-1 p-0 m-0">
																		<span>{rep.name}</span>
																		<span>{rep.email}</span>
																		<span>{rep.address}</span>
																	</div>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</td>
										</tr>
									)}

									<tr className="border-b last:border-b-0 border-border/50">
										<td className="w-[1%] whitespace-nowrap  bg-slate-50 font-semibold text-foreground-dark py-2 px-3">Cases</td>
										<td className="p-3 text-foreground py-2 px-3 group">
											<Link to={`/profile/${user?.user?.id}?status=all`} target="_blank" className="font-semibold text-blue-600 hover:text-blue-500 text-sm">
												Visit {user?.full_name}'s Page
											</Link>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</form>
		</Form>
	);
}

function CasePartyGroup({
	partyType = "Plaintiff",
	children,
}: {
	partyType?: "Plaintiff" | "Defendant";
	children: React.ReactNode;
}) {
	return (
		<PartyTypeContext.Provider value={partyType}>
			<div className="flex flex-col w-full gap-2">
				<div className="flex flex-row items-center w-full gap-2">
					<span className="whitespace-nowrap uppercase text-sm font-semibold text-foreground/50">{partyType}s</span>
					<div className="flex w-full h-[1px] border-t border-border/50" />
				</div>
				<Accordion type="single" collapsible variant="compact">
					{children}
				</Accordion>
			</div>
		</PartyTypeContext.Provider>
	);
}

function CasePartyItem({
	accordionItem,
	partyType,
	partyData,
	defendants,
	caseData,
	showInvitationCode,
	isReadOnly,
}: {
	accordionItem: string;
	defendants: any;
	partyType: string;
	partyData: any;
	caseData: any;
	showInvitationCode: boolean;
	isReadOnly: boolean;
}) {
	const modal = useModal();
	const roleCounts = partyData.counsels.reduce((acc, counsel) => {
		const role = counsel.role || "Unknown";
		acc[role] = (acc[role] || 0) + 1;
		return acc;
	}, {});
	const regenerateKeyCodeMutation = useRegenerateKeyCodeMutation();
	const caseInvitePreviewMutation = useCaseInvitePreviewMutation();
	const [isDownloading, setIsDownloading] = useState(false);

	const handleDownload = async (id) => {
		setIsDownloading(true);
		toast.success("Download started");
		const response = await caseInvitePreviewMutation.mutateAsync({
			case_id: caseData.id,
			case_record_id: id,
		});

		const url = URL.createObjectURL(response);

		downloadFile(url, "invite-letter.pdf");
		setIsDownloading(false);
	};

	const [openPartyPopover, setOpenPartyPopover] = useState(false);

	const handleOpenChange = (state: boolean) => {
		setOpenPartyPopover(state);
	};

	const [openCounselPopoverId, setOpenCounselPopoverId] = useState<string | null>(null);

	const handleCounselPopoverChange = (open: boolean, counselId: string) => {
		if (open) {
			setOpenCounselPopoverId(counselId);
		} else if (openCounselPopoverId === counselId) {
			setOpenCounselPopoverId(null);
		}
	};

	return (
		<AccordionItem value={accordionItem}>
			<AccordionTrigger className="py-2 px-3">
				<div className="inline-flex flex-row items-center w-auto gap-1">
					<Popover open={openPartyPopover} onOpenChange={handleOpenChange}>
						<PopoverTrigger asChild>
							<button
								type="button"
								onMouseEnter={() => setOpenPartyPopover(true)}
								onClick={(e) => {
									setOpenPartyPopover((prev) => !prev);
									e.stopPropagation();
								}}
								// onMouseLeave={() => setTimeout(() => setOpenPartyPopover(false), 200)}
								className=""
							>
								<span className="group inline-flex flex-row items-center gap-1">
									{partyType === "Plaintiff" ? (
										<div className="inline-flex justify-center items-center shrink-0 w-[20px] h-[20px] rounded bg-blue-500">
											<span className="uppercase text-sm font-semibold text-white">P</span>
										</div>
									) : (
										<div className="inline-flex justify-center items-center shrink-0 w-[20px] h-[20px] rounded bg-red-500">
											<span className="uppercase text-sm font-semibold text-white">D</span>
										</div>
									)}
									<span className="text-base font-semibold text-blue-600 group-hover:text-blue-500">{partyData?.full_name}</span>
									{!partyData.can_add_activity && (
										<Tooltip content="Activity Locked" side="bottom">
											<IconLock size={14} />
										</Tooltip>
									)}
								</span>
							</button>
						</PopoverTrigger>
						<PopoverContent onMouseEnter={() => setOpenPartyPopover(true)} onMouseLeave={() => setOpenPartyPopover(false)}>
							<UserInfoCard
								isCounsel={false}
								user={partyData}
								invitationCode={partyData?.invitation_code}
								showInvitationCode={showInvitationCode}
								caseData={caseData}
							/>
						</PopoverContent>
					</Popover>
					<span className="text-base text-foreground/70">({partyData.counsels?.length} Counsels)</span>
				</div>
				<div className="inline-flex ml-auto">
					{!isReadOnly && (
						<DropdownMenu modal={false}>
							<DropdownMenuTrigger asChild>
								<Button
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
									}}
									className="!rounded-full !w-[24px] !h-[24px]"
									size="icon"
									variant="ghost"
								>
									<IconDots size={14} stroke={1.5} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								{partyType !== "Plaintiff" && (
									<HasPermission permissions={[userPermissions.ADD_COUNSEL]}>
										<DropdownMenuItem
											onSelect={(e) => {
												e.preventDefault();
												e.stopPropagation();
												const counselModal = modal.openModal({
													title: "Add New Defendant",
													description: "Case Defendant",
													size: "lg",
													component: (
														<CaseDetailsAddDefendantModal
															caseData={caseData}
															defendants={defendants}
															defendantId={partyData.id}
															onSubmit={(data) => {
																modal.closeModal(counselModal);
															}}
														/>
													),
												});
											}}
										>
											<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
												<IconPlus size={16} stroke={2} />
												<span className="">Add Defendant</span>
											</div>
										</DropdownMenuItem>
									</HasPermission>
								)}
								<DropdownMenuItem onSelect={() => handleDownload(partyData.id)}>
									{caseData.has_counsels && (
										<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
											<IconPlus size={16} stroke={2} />
											<span className="">Download Key</span>
										</div>
									)}
								</DropdownMenuItem>
								<DropdownMenuItem
									onSelect={() => {
										modal.openModal({
											title: "Re-generate Key Code",
											description: "You're about to regenerate this party's key code. Do you want to proceed?",
											type: "confirm",
											variant: "green",
											onConfirm: async () => {
												regenerateKeyCodeMutation.mutateAsync({
													caseRecordId: caseData.id,
													data: partyData,
												});
											},
										});
									}}
								>
									{caseData.has_counsels && (
										<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
											<IconPlus size={16} stroke={2} />
											<span className="">Re-generate Key</span>
										</div>
									)}
								</DropdownMenuItem>
								<HasPermission permissions={[userPermissions.ADD_COUNSEL]}>
									<DropdownMenuItem
										onSelect={(e) => {
											e.preventDefault();
											e.stopPropagation();
											const counselModal = modal.openModal({
												title: "Add New Counsel",
												description: "Case Counsel",
												size: "lg",
												allowClose: false,
												component: (
													<CaseCustomCoCounselModal
														isEdit={false}
														caseData={caseData}
														isPlaintiffCounsel={partyType === "Plaintiff"}
														defendants={defendants}
														onSubmit={(data) => {
															modal.closeModal(counselModal);
														}}
													/>
												),
											});
										}}
									>
										{caseData.has_counsels && (
											<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
												<IconPlus size={16} stroke={2} />
												<span className="">Add Counsel</span>
											</div>
										)}
									</DropdownMenuItem>
								</HasPermission>
								{/* <HasPermission permissions={[userPermissions.ADD_PARTY_COMPLAINT]}> */}
								{partyType !== "Plaintiff" && caseData.allow_post_as && (
									<DropdownMenuItem
										onSelect={(e) => {
											e.preventDefault();
											e.stopPropagation();
											modal.openModal({
												title: `Add ${toOrdinal(Number.parseInt(partyData._depth) + 3)} Party Complaint`,
												size: "lg",
												showHeader: true,
												showFooter: false,
												component: (
													<CasePartyComplaintModal
														caseId={caseData.id}
														defendentId={partyData.id}
														stage={caseData.stage}
														title={`Add ${toOrdinal(Number.parseInt(partyData._depth) + 3)} Party Complaint`}
														caseData={caseData}
														user={partyData}
														depth={Number.parseInt(partyData._depth) + 3}
													/>
												),
											});
										}}
									>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
											<IconPlus size={16} stroke={2} />
											Add {toOrdinal(Number.parseInt(partyData._depth) + 3)} Party Complaint
										</div>
									</DropdownMenuItem>
								)}
								{/* </HasPermission> */}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			</AccordionTrigger>
			<AccordionContent className="p-3">
				{partyData.counsels.length > 0 ? (
					<div className="flex flex-col w-full">
						<span className="text-base text-foreground/70">Counsels ({partyData.counsels.length})</span>
						<div className="flex flex-row flex-wrap">
							{[...partyData.counsels]
								.sort((a, b) => (b.is_lead_counsel ? 1 : 0) - (a.is_lead_counsel ? 1 : 0))
								.map((counsel, index) => (
									<>
										<Popover key={counsel.id} open={openCounselPopoverId === counsel.id} onOpenChange={(open) => handleCounselPopoverChange(open, counsel.id)}>
											<PopoverTrigger asChild>
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														setOpenCounselPopoverId((prev) => (prev === counsel.id ? null : counsel.id));
													}}
													onMouseEnter={() => setOpenCounselPopoverId(counsel.id)}
													// onMouseLeave={() => {
													// 	if (openCounselPopoverId === counsel.id) {
													// 		setTimeout(() => setOpenCounselPopoverId(null), 200);
													// 	}
													// }}
													className=""
												>
													<span className="group inline-flex flex-row items-center gap-1 text-base font-semibold text-blue-600 hover:text-blue-500">
														{counsel.is_lead_counsel && (
															<Badge color="gray" size="xs" className="mr-2">
																Lead
															</Badge>
														)}
														<span className={counsel.removed_at ? "line-through text-red-500" : ""}>{counsel?.full_name}</span>
													</span>
												</button>
											</PopoverTrigger>
											<PopoverContent onMouseEnter={() => setOpenCounselPopoverId(counsel.id)} onMouseLeave={() => setOpenCounselPopoverId(null)}>
												<UserInfoCard
													isCounsel={true}
													user={counsel}
													invitationCode={counsel?.invitation_code}
													showInvitationCode={showInvitationCode}
													caseData={caseData}
												/>
											</PopoverContent>
										</Popover>

										{!counsel.can_add_activity && (
											<Tooltip content="Activity Locked" side="bottom">
												<IconLock className="mt-1 ml-2" size={14} />
											</Tooltip>
										)}
										{index < partyData.counsels.length - 1 && <span className="mr-1 text-foreground font-semibold">,</span>}
									</>
								))}
						</div>
					</div>
				) : (
					<span className="text-foreground/70 text-base">No counsel assigned</span>
				)}
				<span className="text-base text-foreground/70">{caseData.full_name}</span>
			</AccordionContent>
		</AccordionItem>
	);
}

export const ComplexCaseParty = ({ allowPost = false, isReadOnly = false, plaintiffs, defendants, caseData, parties }) => {
	const defendantLabel = caseData.defendant_label;
	const plaintiffLabel = caseData.plaintiff_label;

	const documentTokenMutation = useDocumentConfigMutation();
	const modal = useModal();

	const handleClick = async (attachment) => {
		const fileExtension = attachment.file_name.split(".").pop();
		let response = null;
		if (!["application/pdf", "pdf", "image/jpeg", "image/png", "png", "jpg", "jpeg", "video/mp4", "mp4"].includes(fileExtension)) {
			response = await documentTokenMutation.mutateAsync({
				file_type: fileExtension,
				title: attachment.name,
				url: attachment.original_url,
				mode: "view",
			});
		}

		modal.openModal({
			title: "Default Modal",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <FileViewer attachment={attachment} token={response ? response?.token : null} options={response ? response?.options : null} />,
		});
	};

	const caseDocumentsColumns: ColumnDef<FileRecord>[] = [
		{
			id: "name",
			label: "File Name",
			cellClassName: "",
			cell: ({ data }) => (
				<button type="button" onClick={() => handleClick(data)} className="font-semibold text-blue-600 hover:text-blue-500">
					{data.name}
				</button>
			),
		},
		{
			id: "mime_type",
			label: "File Type",
			cellClassName: "",
			cell: ({ data }) => data.mime_type,
		},
		{
			id: "size",
			label: "File Size",
			cellClassName: "",
			cell: ({ data }) => <span>{formatFileSize(data.size)}</span>,
		},
		{
			id: "date_created",
			label: "Date Created",
			cellClassName: "text-right",
			headerClassName: "text-right",
			cell: ({ data }) => <span>{formatDate(data.created_at, "PPp")}</span>,
		},
	];

	const [files, setFiles] = useState<File[]>([]);
	const uploadMutation = useCaseRecordCaseComplaintMutation();

	const openAmmendModal = (_depth: number) => {
		modal.openModal({
			allowClose: false,
			size: "xl",
			title: "Case Amendment",
			showHeader: true,
			showFooter: false,
			component: <CaseAmmendmentModal title="Case Amendment" data={caseData} _depth={_depth} caseId={caseData.id} />,
		});
	};

	return (
		<div className="flex flex-col w-full pt-4 gap-4">
			<Tabs defaultValue="initial-dispute" variant="compact">
				<TabsList>
					<TabsTrigger value="initial-dispute">Primary Complaint</TabsTrigger>
					{parties.map(([key, array]) => (
						<TabsTrigger key={`tabs_${key}`} value={key}>
							{toOrdinal(Number.parseInt(key) + 2)} Party Complaint
						</TabsTrigger>
					))}
				</TabsList>
				<TabsContent value="initial-dispute">
					<div className="flex flex-col w-full p-4 gap-3 border-b last:border-0 border-dashed border-border">
						<div className="flex flex-col w-full gap-1">
							<span className="text-base font-semibold text-foreground-dark">{caseData.case_title}</span>
						</div>

						<div className="flex flex-col w-full gap-2">
							<CasePartyGroup partyType={plaintiffLabel}>
								{plaintiffs.map((member, index) => (
									<CasePartyItem
										key={`plaintiff_p${index}`}
										accordionItem={`p${index}`}
										partyType="Plaintiff"
										partyData={member}
										defendants={plaintiffs}
										caseData={caseData}
										showInvitationCode={false}
										isReadOnly={isReadOnly}
									/>
								))}
							</CasePartyGroup>
							<CasePartyGroup partyType={defendantLabel}>
								{defendants.map((member, index) => (
									<CasePartyItem
										key={`defendants_${index}`}
										accordionItem={`d${index}`}
										partyType="Defendant"
										defendants={defendants}
										partyData={member}
										caseData={caseData}
										showInvitationCode={false}
										isReadOnly={isReadOnly}
									/>
								))}
								{/* <CasePartyItem accordionItem="d2" /> */}
							</CasePartyGroup>
						</div>
						<div className="flex flex-col w-full gap-2">
							<div className="flex flex-row items-center justify-between w-full p-4 mb-2 gap-4 bg-slate-50">
								<span className="text-base font-bold text-foreground-dark">Complaint and Annexes</span>
								{/* <Button className="relative" type="button" size="sm">
                  <input type="file" multiple className="absolute opacity-0 inset-0" onChange={handleFileChange} />
                  <IconPlus size={14} stroke={2} />
                  <span className="">Add File</span>
                </Button>  */}
							</div>
							<CaseAttachmentsTable
								data={caseData}
								title="Case Complaints"
								counts={0}
								allowAdd={caseData.status === "received"}
								allowRemove={caseData.status === "received"}
								allowPreview={false}
								allowPrint={false}
								allowDownload={false}
							/>
							<CaseAnnexesTable
								data={caseData}
								title="Annexes"
								counts={0}
								allowAdd={caseData.status === "received"}
								allowRemove={caseData.status === "received"}
								allowPreview={false}
								allowPrint={false}
								allowDownload={false}
							/>
							{caseData.allow_post_as && (
								<div className="flex flex-col w-full gap-2">
									<div className="grid grid-cols-8 w-full gap-3 py-3 overflow-y-auto">
										<Button
											className="px-4 py-2 rounded-md"
											type="button"
											variant="danger"
											onClick={() => {
												openAmmendModal(0);
											}}
										>
											Amend
										</Button>
									</div>
								</div>
							)}
						</div>
					</div>
				</TabsContent>

				{parties.map(([key, array]) => (
					<TabsContent key={`content_${key}`} value={key}>
						{array.map((item, index) => (
							<div key={`content_party_${index}`} className="flex flex-col w-full p-4 gap-3 border-b last:border-0 border-dashed border-border">
								<div className="flex flex-col w-full gap-1">
									<span className="text-base font-semibold text-foreground-dark">
										{item.plaintiff[0]?.user?.full_name} VS {item.plaintiff[0]?.defendants.map((d) => d.user?.full_name).join(" AND ")}
									</span>
								</div>

								<div className="flex flex-col w-full gap-2">
									<CasePartyGroup partyType={plaintiffLabel}>
										<CasePartyItem
											key={`plaintiff_${key}_${index}_${array}`}
											accordionItem={`p${index}`}
											partyType="Plaintiff"
											partyData={item.plaintiff[0]}
											caseData={caseData}
											defendants={item.plaintiff}
											showInvitationCode={false}
											isReadOnly={isReadOnly}
										/>
									</CasePartyGroup>
									<CasePartyGroup partyType={defendantLabel}>
										{item.plaintiff[0].defendants.map((member, index) => (
											<CasePartyItem
												key={`plaintiff_${key}_${index}_${array}`}
												accordionItem={`p${index}`}
												partyType="Defendant"
												partyData={member}
												defendants={array}
												caseData={caseData}
												showInvitationCode={false}
												isReadOnly={isReadOnly}
											/>
										))}
									</CasePartyGroup>
								</div>

								<div className="flex flex-col w-full gap-2">
									<div className="flex flex-col w-auto">
										<span className="text-base font-bold text-foreground-dark">Complaint and Annexes</span>
									</div>
									<DataTable
										className="border-collapse"
										data={item?.medias}
										columns={caseDocumentsColumns}
										stickyHead={false}
										stickyCol={false}
										showBorder={true}
										compact={true}
										showToolbar={false}
									/>
								</div>
							</div>
						))}
					</TabsContent>
				))}
			</Tabs>
		</div>
	);
};
